import React, { useState, useEffect } from 'react'
import { Select, Space } from 'antd'
import { ClockCircleOutlined, CloseCircleFilled, SwapRightOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { generateTimeOptions } from './generate-timepicker-time-options'
import IntlMessages from '../utils/IntlMessages'
import { BaseInfo } from 'rc-picker/lib/interface'
import { CustomTimeRangePickerProps, ITimeOption } from './types'
import { DEFAULT_WORKING_HOURS_IN_DAY } from '../../data/app-parameters'
import { getUserWorkingHoursDependingOnType, getUserWorkingHoursOnDay } from '../../functions/work-week'
import { ShortestLeaveIntervalEnum } from '../../types/leave-policy'
import { HourFormatEnum } from '../../types/user'
import { safeToISOString, safeDayjs } from '../../functions/safe-date-format'

const CustomTimeRangePicker: React.FC<CustomTimeRangePickerProps> = ({
  form,
  shortestLeaveInterval,
  workHours,
  selectedDate,
  shouldResetTime,
  hourFormat,
  minuteStep = 60,
  isHalfDayHoliday,
  onChange,
  style,
  editTimes,
  setShouldResetTime,
  generateAllTimeOptions,
}) => {
  const [startTime, setStartTime] = useState<ITimeOption | null>(null)
  const [endTime, setEndTime] = useState<ITimeOption | null>(null)
  const [hovered, setHovered] = useState<boolean>(false)
  const [hourFormatString] = useState<string>(hourFormat === HourFormatEnum.twelve ? 'hh:mm A' : 'HH:mm')
  const [startTimes, setStartTimes] = useState<ITimeOption[]>([])
  const [endTimes, setEndTimes] = useState<ITimeOption[]>([])

  useEffect(() => {
    if (editTimes && selectedDate) {
      const {
        startHour,
        startMinute,
        endHour,
        endMinute,
      } = editTimes
      const startTime = dayjs().hour(startHour).minute(startMinute as number)
      const endTime = dayjs().hour(endHour).minute(endMinute as number)
      const startTimeObj = {
        key: safeToISOString(startTime),
        label: startTime.format(hourFormatString),
        value: safeToISOString(startTime),
      }

      const endTimeObj = {
        key: safeToISOString(endTime),
        label: endTime.format(hourFormatString),
        value: safeToISOString(endTime),
      }
      setStartTime(startTimeObj)
      const startTimeValue = safeDayjs(startTimeObj.value, startTime)

      const endTimeOptions = generateTimeOptions({
        type: 'end',
        shortestLeaveInterval,
        startTimeValue,
        hourFormatString,
        minuteStep,
        workingHoursInDay: generateAllTimeOptions ? 24 : getUserWorkingHoursOnDay(getUserWorkingHoursDependingOnType(workHours) || [], selectedDate.toDate()),
        isHalfDayHoliday: Boolean(isHalfDayHoliday()),
      })
      setEndTimes(endTimeOptions)
      // We need to wait for the next render cycle to set the end time :)
      setTimeout(() => {
        setEndTime(endTimeObj)
      }, 0)

      updateForm([startTime, endTime])
    }
  }, [editTimes])

  useEffect(() => {
    if (selectedDate) {

      const startTimeOptions = generateTimeOptions({
        type: 'start',
        shortestLeaveInterval,
        hourFormatString,
        minuteStep,
      })
      setStartTimes(startTimeOptions)
      
      if (!editTimes) {
        const initialStartTimeObj = dayjs().hour(8).minute(0).second(0).millisecond(0)
        const initialStartTime = {
          value: safeToISOString(initialStartTimeObj),
          label: initialStartTimeObj.format(hourFormatString),
          key: safeToISOString(initialStartTimeObj),
        }
        setStartTime(initialStartTime)

        const startTimeValue = safeDayjs(initialStartTime?.value, initialStartTimeObj)

        // Generate end times immediately after setting start time
        const endTimeOptions = generateTimeOptions({
          type: 'end',
          shortestLeaveInterval,
          startTimeValue,
          hourFormatString,
          minuteStep,
          workingHoursInDay: generateAllTimeOptions ? 24 : getUserWorkingHoursOnDay(getUserWorkingHoursDependingOnType(workHours) || [], selectedDate.toDate()),
          isHalfDayHoliday: Boolean(isHalfDayHoliday()),
        })
        setEndTimes(endTimeOptions)
        handleTimeSelect([safeDayjs(initialStartTime.value, initialStartTimeObj)], undefined, { range: 'start' })
      }
      if (shouldResetTime) {
        setShouldResetTime(false)
      }
    }
    //Selected date is date object so use effect will not be called(valueOf is returning timestamp)
  }, [selectedDate?.valueOf(), editTimes, shouldResetTime])

  useEffect(() => {
    if (startTime && selectedDate) {
      const existingEndTime = endTime
      let newEndTime
      const startTimeValue = startTime.value

      const endTimeOptions = generateTimeOptions({
        type: 'end',
        shortestLeaveInterval,
        startTimeValue: safeDayjs(startTimeValue),
        hourFormatString,
        minuteStep,
        workingHoursInDay: generateAllTimeOptions ? 24 : getUserWorkingHoursOnDay(getUserWorkingHoursDependingOnType(workHours) || [], selectedDate.toDate()),
        isHalfDayHoliday: Boolean(isHalfDayHoliday()),
      })
      setEndTimes(endTimeOptions)
      if (shouldUpdateEndTime(startTime, existingEndTime)) {
        newEndTime = endTimeOptions.filter(option => !option.disabled)[0]
        setEndTime(newEndTime as ITimeOption)
      }
      const endTimeValue = newEndTime?.value || existingEndTime?.value

      updateParentValue(startTimeValue, endTimeValue as string)
      handleTimeSelect([safeDayjs(startTimeValue), safeDayjs(endTimeValue as string)], undefined, { range: 'end' })
    }
  }, [startTime, editTimes])


  const shouldUpdateEndTime = (
    startTime: ITimeOption,
    existingEndTime: ITimeOption | null
  ) => {
    if (!existingEndTime) return true

    const startMoment = safeDayjs(startTime.value).second(0).millisecond(0)
    const endMoment = safeDayjs(existingEndTime.value).second(0).millisecond(0)

    // Calculate if current end time is within valid range (in minutes)
    const diffMinutes = endMoment.diff(startMoment, 'minute')
    let maxWorkingMinutes = DEFAULT_WORKING_HOURS_IN_DAY * 60
    if (selectedDate) {
      const workingHours = generateAllTimeOptions ? 24 : getUserWorkingHoursOnDay(getUserWorkingHoursDependingOnType(workHours) || [], selectedDate.toDate())
      maxWorkingMinutes = workingHours * 60
    }

    const isWithinWorkingWindow = diffMinutes > 0 && diffMinutes <= maxWorkingMinutes

    return !isWithinWorkingWindow || startMoment.isAfter(endMoment) || startMoment.isSame(endMoment)
  }

  const handleTimeSelect = (dates: Dayjs[] | null, dateStrings?: string[], info?: BaseInfo) => {
    if (!dates || !selectedDate) {
      return
    }
    const start = dates[0]
    if (start && info?.range === 'start' && shortestLeaveInterval === ShortestLeaveIntervalEnum.halfDay) {
      // Get user working hours and available hours for a selected date
      const workingHours = getUserWorkingHoursOnDay(
        getUserWorkingHoursDependingOnType(workHours) || [], selectedDate.toDate() // start date will always be today, we need selectedDate
      )
      const halfDayMinutes = Math.round((workingHours * 60) / 2)
      const endTime = start.add(halfDayMinutes, 'minute')
      updateForm([start, endTime])
      setEndTime({
        key: safeToISOString(endTime),
        label: endTime.format(hourFormat === HourFormatEnum.twelve ? 'hh:mm A' : 'HH:mm'),
        value: safeToISOString(endTime),
      })
      return
    }
    updateForm(dates)
  }

  const handleStartTimeChange = (startTimeOption: ITimeOption) => {
    setStartTime(startTimeOption)
    if (endTime && safeDayjs(startTimeOption.value).isAfter(safeDayjs(endTime.value))) {
      setEndTime(null)
    }
    updateParentValue(startTimeOption.value, endTime?.value as string)
    handleTimeSelect([safeDayjs(startTimeOption.value)], undefined, { range: 'start' })
  }

  const handleEndTimeChange = (endTimeOption: ITimeOption) => {
    setEndTime(endTimeOption)
    updateParentValue(startTime?.value as string, endTimeOption.value)
    handleTimeSelect([safeDayjs(startTime?.value), safeDayjs(endTimeOption.value)], undefined, { range: 'end' })
  }

  const updateParentValue = (start: string | null, end: string | null) => {
    const formatTime = (time: string | null) => (time ? safeDayjs(time).get('hours').toString() : null)

    const startTime = formatTime(start)
    const endTime = formatTime(end)

    onChange?.({ startTime, endTime })
  }

  const handleClear = () => {
    updateForm([null, null])
    setStartTime(null)
    setEndTime(null)
    onChange?.({ startTime: null, endTime: null })
    handleTimeSelect(null, undefined, { range: 'start' })
  }

  const updateForm = (dates: (Dayjs | null)[]) => {
    if (!dates) {
      return
    }
    form.setFieldsValue({
      range: dates,
    })
  }

  const handleOnDropdownVisibleChange = (open: boolean, timeLabel: string | undefined) => {
    if (open) {
      setTimeout(() => {
        const container = document.querySelector('.ant-select-dropdown:not(.ant-select-dropdown-hidden)') as HTMLElement
        if (container) {
          scrollToSelectedOption(container, timeLabel || '')
        }
      }, 325)
    }
  }

  const scrollToSelectedOption = (container: HTMLElement, timeLabel: string) => {
    const selectedOption = Array.from(container.querySelectorAll('.ant-select-item-option-content')).find(
      (el) => el.textContent === timeLabel
    ) as HTMLElement | undefined

    if (selectedOption) {
      selectedOption.scrollIntoView({
        block: 'center',
      })
    }
  }

  const handleOnMouseEnter = () => {
    if (startTime && endTime) {
      setHovered(true)
    }
  }

  const handleOnMouseLeave = () => {
    if (hovered) {
      setHovered(false)
    }
  }

  return (
    <div
      className="custom-time-range-picker"
      style={style}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
    >
      <Space>
        <Select<string, ITimeOption>
          popupMatchSelectWidth={100}
          className="time-select"
          style={{ width: 130 }}
          placeholder={<IntlMessages id="components.leaveForm.startTime" />}
          value={startTime as unknown as string}
          labelInValue
          onChange={v => handleStartTimeChange(v as unknown as ITimeOption)}
          options={startTimes}
          disabled={!selectedDate || shouldResetTime}
          suffixIcon={null}
          onDropdownVisibleChange={(open) => handleOnDropdownVisibleChange(open, startTime?.label)}
        />
        <SwapRightOutlined />
        <div className="end-time-container">
          <Select<string, ITimeOption>
            popupMatchSelectWidth={100}
            style={{ width: 130 }}
            className="time-select"
            placeholder={<IntlMessages id="components.leaveForm.endTime" />}
            value={endTime as unknown as string}
            labelInValue
            onChange={v => handleEndTimeChange(v as unknown as ITimeOption)}
            options={endTimes}
            suffixIcon={null}
            disabled={!startTime || shouldResetTime || !selectedDate || shortestLeaveInterval === ShortestLeaveIntervalEnum.halfDay}
            onDropdownVisibleChange={(open) => handleOnDropdownVisibleChange(open, endTime?.label)}
          />
          {hovered
            ? <CloseCircleFilled className='clear-icon' onClick={handleClear} />
            : <ClockCircleOutlined className='clock-icon' />
          }
        </div>
      </Space>
    </div>
  )
}

export default CustomTimeRangePicker